import { useMemo, useState } from "react";
import {
  Avatar,
  Button,
  mergeClasses,
  Tooltip,
} from "@fluentui/react-components";
import { EditRegular } from "@fluentui/react-icons";
import { useDispatch } from "react-redux";
import { updateProjectIqWorkspace } from "apps/project-iq/api";
import { EditProjectIqWorkspaceDialog } from "apps/project-iq/components/EditProjectIqWorkspaceDialog/EditProjectIqWorkspaceDialog";
import { usePortfolios } from "apps/project-iq/hooks/usePortfolios";
import { usePractices } from "apps/project-iq/hooks/usePractices";
import { ICreateOrUpdateProjectIQWorkspace } from "models/IProjectIQWorkspace";
import { IUserDetails } from "models/IUserDetails";
import { updateProjectIqWorkspace as updateProjectIqWorkspaceAction } from "redux/projectIqWorkspacesSlice";
import { DeliverableStatus } from "models/IProjectIQWorkspace";
import {
  MilestoneStatus,
  RaidCategory,
  RaidSeverity,
  RaidStatus,
} from "apps/project-iq/components/WorkspacePlan/WorkspacePlan.types";
import {
  computeScheduleProgress,
  countBy,
  formatDate,
} from "apps/project-iq/utils/workspaceOverviewUtils";
import { useStyles } from "./WorkspaceOverview.styles";
import { IWorkspaceOverviewProps } from "./WorkspaceOverview.types";
import {
  AVATAR_COLORS,
  DEFAULT_RAG_DIMENSIONS,
  DELIVERABLE_STATUS_LABELS,
  MAX_VISIBLE_AVATARS,
  MILESTONE_STATUS_LABELS,
  RAG_STATUS_COLOR,
  RAID_SEVERITY_LABELS,
  RAID_STATUS_LABELS,
} from "./WorkspaceOverview.constants";

const NONE_SELECTED_LABEL = "None selected";

export const WorkspaceOverview = ({
  workspace,
  onNavigateToTab: _onNavigateToTab,
}: IWorkspaceOverviewProps): JSX.Element => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const { portfolios } = usePortfolios();
  const { practices } = usePractices();

  const portfolioLabel = useMemo(
    () =>
      portfolios.find((p) => p.id === workspace.portfolioId)?.label ??
      NONE_SELECTED_LABEL,
    [portfolios, workspace.portfolioId],
  );
  const practiceLabel = useMemo(() => {
    const labels = (workspace.practiceIds ?? [])
      .map((id) => practices.find((p) => p.id === id)?.label)
      .filter((label): label is string => Boolean(label));
    return labels.length > 0 ? labels.join(", ") : NONE_SELECTED_LABEL;
  }, [practices, workspace.practiceIds]);

  const members = useMemo<IUserDetails[]>(() => {
    const result: IUserDetails[] = [];
    const seen = new Set<string>();
    for (const m of [
      ...(workspace.owners ?? []),
      ...(workspace.teamMembers ?? []),
    ]) {
      if (m?.userId && !seen.has(m.userId)) {
        seen.add(m.userId);
        result.push(m);
      }
    }
    return result;
  }, [workspace.owners, workspace.teamMembers]);
  const visibleMembers = members.slice(0, MAX_VISIBLE_AVATARS);
  const extraMembers = members.length - visibleMembers.length;

  const milestones = workspace.projectPlan?.milestones ?? [];
  const deliverables = workspace.deliverables ?? [];
  const raidEntries = workspace.projectPlan?.raidEntries ?? [];

  const scheduleProgress = useMemo(() => {
    const schedule = workspace.projectPlan?.ragDimensions?.find(
      (d) => d.id === "schedule",
    );
    if (!schedule?.startDate || !schedule?.endDate) return null;
    return computeScheduleProgress(schedule.startDate, schedule.endDate);
  }, [workspace.projectPlan?.ragDimensions]);

  const milestoneCounts = useMemo(
    () => countBy(milestones, (m) => m.status),
    [milestones],
  );
  const deliverableCounts = useMemo(
    () => countBy(deliverables, (d) => d.status),
    [deliverables],
  );
  const raidByStatus = useMemo(
    () => countBy(raidEntries, (r) => r.status),
    [raidEntries],
  );

  const sortedMilestones = useMemo(
    () =>
      [...milestones].sort((a, b) => {
        const aTime = a.dueDate ? new Date(a.dueDate).getTime() : 0;
        const bTime = b.dueDate ? new Date(b.dueDate).getTime() : 0;
        return bTime - aTime;
      }),
    [milestones],
  );
  const sortedDeliverables = useMemo(
    () => [...deliverables].sort((a, b) => b.updatedAt - a.updatedAt),
    [deliverables],
  );
  const sortedRaidEntries = useMemo(
    () =>
      [...raidEntries].sort((a, b) => {
        const aTime = a.dueDate ? new Date(a.dueDate).getTime() : 0;
        const bTime = b.dueDate ? new Date(b.dueDate).getTime() : 0;
        return bTime - aTime;
      }),
    [raidEntries],
  );

  const [milestoneStatusFilter, setMilestoneStatusFilter] = useState<
    string | null
  >(null);
  const [deliverableStatusFilter, setDeliverableStatusFilter] = useState<
    string | null
  >(null);
  const [raidStatusFilter, setRaidStatusFilter] = useState<string | null>(null);

  const filteredMilestones = useMemo(
    () =>
      milestoneStatusFilter
        ? sortedMilestones.filter((m) => m.status === milestoneStatusFilter)
        : sortedMilestones,
    [sortedMilestones, milestoneStatusFilter],
  );
  const filteredDeliverables = useMemo(
    () =>
      deliverableStatusFilter
        ? sortedDeliverables.filter((d) => d.status === deliverableStatusFilter)
        : sortedDeliverables,
    [sortedDeliverables, deliverableStatusFilter],
  );
  const filteredRaidEntries = useMemo(
    () =>
      raidStatusFilter
        ? sortedRaidEntries.filter((r) => r.status === raidStatusFilter)
        : sortedRaidEntries,
    [sortedRaidEntries, raidStatusFilter],
  );

  const milestonePillClass: Record<MilestoneStatus, string> = {
    not_started: styles.pillUpcoming,
    in_progress: styles.pillProgress,
    done: styles.pillComplete,
  };

  const deliverablePillClass: Record<DeliverableStatus, string> = {
    not_started: styles.pillUpcoming,
    in_progress: styles.pillProgress,
    done: styles.pillComplete,
  };

  const raidPillClass: Record<RaidSeverity, string> = {
    high: styles.pillHigh,
    medium: styles.pillMedium,
    low: styles.pillLow,
  };

  const raidStatusPillClass: Record<RaidStatus, string> = {
    not_started: styles.pillUpcoming,
    in_progress: styles.pillProgress,
    closed: styles.pillComplete,
  };

  const raidTypeBadgeClass: Record<RaidCategory, string> = {
    risk: styles.raidTypeRisk,
    action: styles.raidTypeAction,
    issue: styles.raidTypeIssue,
    decision: styles.raidTypeDecision,
  };

  const handleSaveWorkspace = async (
    id: string,
    payload: ICreateOrUpdateProjectIQWorkspace,
  ) => {
    const updated = await updateProjectIqWorkspace({ id, workspace: payload });
    dispatch(updateProjectIqWorkspaceAction(updated));
    return updated;
  };

  const handleEditDismiss = () => {
    setIsEditOpen(false);
  };

  return (
    <div className={styles.root}>
      <div className={styles.body}>
        <div className={styles.content}>
          <section className={mergeClasses(styles.card, styles.hero)}>
            <div className={styles.heroBody}>
              <div className={styles.heroMain}>
                <div className={styles.heroMainHeader}>
                  <div>
                    <h2 className={styles.heroTitle}>
                      {workspace.name}
                      {workspace.clientName && (
                        <span className={styles.clientNameInline}>
                          {" "}
                          — {workspace.clientName}
                        </span>
                      )}
                    </h2>
                  </div>

                  <Button
                    appearance="transparent"
                    icon={<EditRegular />}
                    onClick={() => setIsEditOpen(true)}
                  />
                </div>

                <div className={styles.portfolioPracticeRow}>
                  <span>Portfolio: {portfolioLabel}</span>
                  <span />
                  <span>Practice: {practiceLabel}</span>
                </div>

                {workspace.description && (
                  <Tooltip
                    content={workspace.description}
                    relationship="description"
                  >
                    <p className={styles.description}>
                      {workspace.description}
                    </p>
                  </Tooltip>
                )}
              </div>

              <div
                className={mergeClasses(
                  styles.heroSide,
                  !scheduleProgress && styles.heroSideNoProgress,
                )}
              >
                {scheduleProgress && (
                  <div className={styles.progressBlock}>
                    <div className={styles.progressLabelRow}>
                      <span className={styles.progressLabel}>
                        {scheduleProgress.label}
                      </span>
                      <span className={styles.progressPercent}>
                        {scheduleProgress.percent}%
                      </span>
                    </div>
                    <div className={styles.progressTrack}>
                      <div
                        className={styles.progressFill}
                        style={{ width: `${scheduleProgress.percent}%` }}
                      />
                    </div>
                  </div>
                )}

                {members.length > 0 && (
                  <div className={styles.members}>
                    <div className={styles.avatarGroup}>
                      {visibleMembers.map((m, i) => (
                        <Avatar
                          key={m.userId}
                          name={m.displayName}
                          size={28}
                          className={styles.avatar}
                          initials={{
                            style: {
                              backgroundColor:
                                AVATAR_COLORS[i % AVATAR_COLORS.length],
                              color: "#ffffff",
                            },
                          }}
                        />
                      ))}
                      {extraMembers > 0 && (
                        <span className={styles.avatarMore}>
                          +{extraMembers}
                        </span>
                      )}
                    </div>
                    <span className={styles.membersCount}>
                      {members.length} member{members.length === 1 ? "" : "s"}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </section>

          <div className={styles.sectionTitleRow}>
            <span className={styles.sectionTitle}>Engagement Plan</span>
          </div>
          <div className={styles.statusGrid}>
            {(
              workspace.projectPlan?.ragDimensions ?? DEFAULT_RAG_DIMENSIONS
            ).map((dimension) => (
              <div key={dimension.id} className={styles.statusTile}>
                <span className={styles.statusLabel}>{dimension.label}</span>
                <div className={styles.statusValueRow}>
                  <span
                    className={styles.statusDot}
                    style={{
                      backgroundColor: RAG_STATUS_COLOR[dimension.status],
                    }}
                  />
                  <span className={styles.statusValue}>
                    {dimension.statusText}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.twoCol}>
            {/* Milestones */}
            <section className={mergeClasses(styles.card, styles.listCard)}>
              <div className={styles.cardHeaderRow}>
                <span className={styles.cardTitle}>Milestones</span>
                {(
                  Object.entries(MILESTONE_STATUS_LABELS) as [
                    MilestoneStatus,
                    string,
                  ][]
                )
                  .filter(([status]) => (milestoneCounts[status] ?? 0) > 0)
                  .map(([status, label]) => (
                    <span
                      key={status}
                      role="button"
                      tabIndex={0}
                      aria-pressed={milestoneStatusFilter === status}
                      aria-label={`Filter by ${MILESTONE_STATUS_LABELS[status]}`}
                      className={mergeClasses(
                        styles.counterChip,
                        styles.counterChipClickable,
                        milestonePillClass[status],
                        milestoneStatusFilter === status &&
                          styles.counterChipActive,
                      )}
                      onClick={() =>
                        setMilestoneStatusFilter((prev) =>
                          prev === status ? null : status,
                        )
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setMilestoneStatusFilter((prev) =>
                            prev === status ? null : status,
                          );
                        }
                      }}
                    >
                      {milestoneCounts[status]} {label}
                    </span>
                  ))}
              </div>
              {filteredMilestones.length > 0 ? (
                <div
                  className={styles.scrollableList}
                  role="list"
                  aria-label="Milestones"
                  tabIndex={0}
                >
                  {filteredMilestones.map((milestone) => (
                    <div
                      key={milestone.id}
                      className={styles.milestoneRow}
                      role="listitem"
                    >
                      <div className={styles.milestoneTopRow}>
                        <div className={styles.milestoneTitleLine}>
                          <Tooltip
                            content={milestone.name}
                            relationship="label"
                          >
                            <span className={styles.milestoneName}>
                              {milestone.name}
                            </span>
                          </Tooltip>
                          {milestone.description && (
                            <span className={styles.itemDescription}>
                              {" – "}
                              {milestone.description}
                            </span>
                          )}
                        </div>
                        <span
                          className={mergeClasses(
                            styles.pill,
                            milestonePillClass[milestone.status],
                          )}
                        >
                          {MILESTONE_STATUS_LABELS[milestone.status]}
                        </span>
                      </div>
                      {milestone.dueDate && (
                        <span className={styles.milestoneDue}>
                          Due {formatDate(milestone.dueDate)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : milestones.length > 0 ? (
                <p className={styles.emptyHint}>
                  No milestones match the selected filter.
                </p>
              ) : (
                <p className={styles.emptyHint}>No milestones yet</p>
              )}
            </section>

            {/* Deliverables */}
            <section className={mergeClasses(styles.card, styles.listCard)}>
              <div className={styles.cardHeaderRow}>
                <span className={styles.cardTitle}>Deliverables</span>
                {(
                  Object.entries(DELIVERABLE_STATUS_LABELS) as [
                    DeliverableStatus,
                    string,
                  ][]
                )
                  .filter(([status]) => (deliverableCounts[status] ?? 0) > 0)
                  .map(([status, label]) => (
                    <span
                      key={status}
                      role="button"
                      tabIndex={0}
                      aria-pressed={deliverableStatusFilter === status}
                      aria-label={`Filter by ${DELIVERABLE_STATUS_LABELS[status]}`}
                      className={mergeClasses(
                        styles.counterChip,
                        styles.counterChipClickable,
                        deliverablePillClass[status],
                        deliverableStatusFilter === status &&
                          styles.counterChipActive,
                      )}
                      onClick={() =>
                        setDeliverableStatusFilter((prev) =>
                          prev === status ? null : status,
                        )
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setDeliverableStatusFilter((prev) =>
                            prev === status ? null : status,
                          );
                        }
                      }}
                    >
                      {deliverableCounts[status]} {label}
                    </span>
                  ))}
              </div>
              {filteredDeliverables.length > 0 ? (
                <div
                  className={styles.scrollableList}
                  role="list"
                  aria-label="Deliverables"
                  tabIndex={0}
                >
                  {filteredDeliverables.map((deliverable) => (
                    <div
                      key={deliverable.id}
                      className={styles.milestoneRow}
                      role="listitem"
                    >
                      <div className={styles.milestoneTopRow}>
                        <div className={styles.milestoneTitleLine}>
                          <Tooltip
                            content={deliverable.name}
                            relationship="label"
                          >
                            <span className={styles.milestoneName}>
                              {deliverable.name}
                            </span>
                          </Tooltip>
                          {deliverable.description && (
                            <span className={styles.itemDescription}>
                              {" – "}
                              {deliverable.description}
                            </span>
                          )}
                        </div>
                        <span
                          className={mergeClasses(
                            styles.pill,
                            deliverablePillClass[deliverable.status],
                          )}
                        >
                          {DELIVERABLE_STATUS_LABELS[deliverable.status]}
                        </span>
                      </div>
                      {(deliverable.owner || deliverable.reviewer) && (
                        <span className={styles.milestoneDue}>
                          {[
                            deliverable.owner && `Owner: ${deliverable.owner}`,
                            deliverable.reviewer &&
                              `Reviewer: ${deliverable.reviewer}`,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : deliverables.length > 0 ? (
                <p className={styles.emptyHint}>
                  No deliverables match the selected filter.
                </p>
              ) : (
                <p className={styles.emptyHint}>No deliverables yet</p>
              )}
            </section>

            {/* RAID Log */}
            <section className={mergeClasses(styles.card, styles.listCard)}>
              <div className={styles.cardHeaderRow}>
                <span className={styles.cardTitle}>RAID Log</span>
                {(Object.entries(RAID_STATUS_LABELS) as [RaidStatus, string][])
                  .filter(([status]) => (raidByStatus[status] ?? 0) > 0)
                  .map(([status, label]) => (
                    <span
                      key={status}
                      role="button"
                      tabIndex={0}
                      aria-pressed={raidStatusFilter === status}
                      aria-label={`Filter by status: ${RAID_STATUS_LABELS[status]}`}
                      className={mergeClasses(
                        styles.counterChip,
                        styles.counterChipClickable,
                        raidStatusPillClass[status],
                        raidStatusFilter === status && styles.counterChipActive,
                      )}
                      onClick={() =>
                        setRaidStatusFilter((prev) =>
                          prev === status ? null : status,
                        )
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setRaidStatusFilter((prev) =>
                            prev === status ? null : status,
                          );
                        }
                      }}
                    >
                      {raidByStatus[status]} {label}
                    </span>
                  ))}
              </div>
              {filteredRaidEntries.length > 0 ? (
                <div
                  className={styles.scrollableList}
                  role="list"
                  aria-label="RAID log entries"
                  tabIndex={0}
                >
                  {filteredRaidEntries.map((entry) => (
                    <div
                      key={entry.id}
                      className={styles.raidRow}
                      role="listitem"
                    >
                      <div className={styles.raidTopRow}>
                        <Tooltip content={entry.name} relationship="label">
                          <span className={styles.raidText}>{entry.name}</span>
                        </Tooltip>
                        <div className={styles.raidPillsRow}>
                          <span
                            className={mergeClasses(
                              styles.pill,
                              raidTypeBadgeClass[entry.category],
                            )}
                          >
                            {entry.category.toUpperCase()}
                          </span>
                          <span
                            className={mergeClasses(
                              styles.pill,
                              raidPillClass[entry.severity],
                            )}
                          >
                            {RAID_SEVERITY_LABELS[entry.severity]}
                          </span>
                          <span
                            className={mergeClasses(
                              styles.pill,
                              raidStatusPillClass[entry.status],
                            )}
                          >
                            {RAID_STATUS_LABELS[entry.status]}
                          </span>
                        </div>
                      </div>
                      {(entry.owner || entry.dueDate) && (
                        <span className={styles.raidMeta}>
                          {[
                            entry.owner,
                            entry.dueDate && `Due ${formatDate(entry.dueDate)}`,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : raidEntries.length > 0 ? (
                <p className={styles.emptyHint}>
                  No RAID entries match the selected filter.
                </p>
              ) : (
                <p className={styles.emptyHint}>No RAID entries yet</p>
              )}
            </section>
          </div>
        </div>

        <EditProjectIqWorkspaceDialog
          isOpen={isEditOpen}
          workspace={workspace}
          onDismiss={handleEditDismiss}
          onSave={handleSaveWorkspace}
        />
      </div>
    </div>
  );
};
